import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Stack,
  Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import { MaterialReactTable } from "material-react-table";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";

import Layout from "../../layout/Layout";
import { apiData } from "../../services/api_service";
import { fetchSalesTargetsAPI, fetchSalesPersonsAPI, saveSalesTargetAPI } from "../../config";
import { formatAED } from "../../utils/format";
import SalesTargetModal from "./SalesTargetModal";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

// Modern Shiny Shimmer Skeleton Component
const ShinySkeleton = ({ width, height, borderRadius = 6, style = {} }) => (
  <Box
    sx={{
      width: width || "100%",
      height: height || 20,
      borderRadius: typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius,
      background: "linear-gradient(90deg, #e2e8f0 0%, #f8fafc 35%, #ffffff 50%, #f8fafc 65%, #e2e8f0 100%)",
      backgroundSize: "400% 100%",
      animation: "shinyShimmer 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite",
      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
      "@keyframes shinyShimmer": {
        "0%": { backgroundPosition: "100% 0%" },
        "100%": { backgroundPosition: "0% 0%" },
      },
      ...style,
    }}
  />
);

const monthOptions = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const currentYear = new Date().getFullYear();
const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

const SalesTargetList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin =
    Number(userData?.role_id || userData?.position_id || userData?.position) === 377 ||
    Number(userData?.role_id) === 1 ||
    String(userData?.role_name || userData?.role || "").toLowerCase().includes("admin") ||
    String(userData?.position_name || userData?.position || "").toLowerCase().includes("admin") ||
    userData?.user_type === "admin";

  useEffect(() => {
    if (!isAdmin) {
      navigate("/reports/sales-target-report");
    }
  }, [isAdmin, navigate]);

  // Filters
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  // Targets & Master Lists
  const [targets, setTargets] = useState([]);
  const [salesPersonsList, setSalesPersonsList] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchSalesPersons = async () => {
    try {
      const res = await apiData("get", fetchSalesPersonsAPI);
      const list = res?.data?.data || res?.data || [];
      if (Array.isArray(list)) {
        setSalesPersonsList(list);
      }
    } catch (err) {
      console.error("Error fetching sales persons list:", err);
    }
  };

  useEffect(() => {
    fetchSalesPersons();
  }, []);

  const fetchTargetsData = async () => {
    try {
      setLoading(true);
      const payload = { year, month };
      const res = await apiData("get", fetchSalesTargetsAPI, payload);
      const data = res?.data?.data || res?.data || {};
      const list = data?.targets || [];
      setTargets(Array.isArray(list) ? list : []);

      if (salesPersonsList.length === 0 && list.length > 0) {
        const spList = list.map((item) => ({
          sales_person_id: item.sales_person_id,
          employee_name: item.employee_name,
          employee_no: item.employee_no,
        }));
        setSalesPersonsList(spList);
      }
    } catch (err) {
      console.error("Fetch Sales Targets Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTargetsData();
  }, [year, month]);

  // Removing a target saves an explicit 0 for that month, which also stops an
  // earlier month's target from being inherited. The person shows as "No Target".
  const removeRef = useRef(null);
  const handleRemove = async (row) => {
    if (!window.confirm(`Remove the target for ${row.employee_name} (${monthOptions.find((m) => m.value === month)?.label} ${year})?`)) return;
    try {
      const res = await apiData("post", saveSalesTargetAPI, {
        sales_person_id: row.sales_person_id,
        year,
        month,
        target_amount: 0,
        notes: "Target removed",
      });
      if (res?.success || res?.statusCode === 200) fetchTargetsData();
      else alert(res?.msg || "Failed to remove target");
    } catch (e) {
      alert("Failed to remove target");
    }
  };
  removeRef.current = handleRemove;

  const handleEdit = (row) => {
    setSelectedTarget(row);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setSelectedTarget(null);
    setIsModalOpen(true);
  };

  // Exports the rows the table is showing (its search / column filters and sort, all pages).
  const exportToExcel = (table) => {
    const sourceRows = table && typeof table.getPrePaginationRowModel === "function" ? table.getPrePaginationRowModel().rows.map((r) => r.original) : targets;
    const excelData = sourceRows.map((t) => ({
      "Salesperson ID": t.sales_person_id,
      "Salesperson Name": t.employee_name,
      "Employee No": t.employee_no || "-",
      Position: t.position_name || "-",
      Year: t.year,
      Month: monthOptions.find((m) => m.value === t.month)?.label || t.month,
      "Target Amount (AED)": Number(t.target_amount) || 0,
      "Target Source": t.source === "explicit" ? "Explicit" : t.source === "inherited" ? `Inherited (${t.inherited_period})` : "Default",
      Notes: t.notes || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    fitColumns(worksheet);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Targets");
    writeXlsxFrozen(XLSX, workbook, `Sales_Targets_${year}_Month_${month}.xlsx`);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "employee_name",
        header: "Sales Representative",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: "#e0f2fe", color: "#0284c7", fontWeight: 700, width: 36, height: 36, fontSize: "0.9rem" }}>
              {row.original.employee_name?.charAt(0) || "S"}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {row.original.employee_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Code: {row.original.employee_no || "N/A"} | {row.original.position_name || "Sales Executive"}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        accessorKey: "target_amount",
        header: "Target Amount",
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontWeight: 800, color: "#1e293b" }}>
            {formatAED(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "source",
        header: "Target Source",
        Cell: ({ row }) => {
          const src = row.original.source;
          if (src === "explicit") {
            return <Chip label="Explicit Month Target" size="small" color="primary" variant="filled" sx={{ fontWeight: 700 }} />;
          } else if (src === "inherited") {
            return (
              <Chip
                label={`Inherited (${row.original.inherited_period})`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            );
          }
          return <Chip label="Not Set" size="small" color="default" variant="outlined" sx={{ fontWeight: 600 }} />;
        },
      },
      {
        accessorKey: "notes",
        header: "Notes / Remarks",
        Cell: ({ cell }) => (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: cell.getValue() ? "normal" : "italic" }}>
            {cell.getValue() || "No remarks"}
          </Typography>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => (
          <>
            <Tooltip title="Set / Modify Target">
              <IconButton color="primary" onClick={() => handleEdit(row.original)} size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {Number(row.original.target_amount) > 0 && (
              <Tooltip title="Remove target (set to 0)">
                <IconButton color="error" onClick={() => removeRef.current && removeRef.current(row.original)} size="small">
                  <RemoveCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        ),
      },
    ],
    []
  );

  return (
    <Layout>
      <Box className="employee-form bold-header">
        {/* Header — same layout as the other list / report pages */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography className="page_title" textAlign="left">
              Monthly Sales Target Management
            </Typography>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
              <Link color="inherit" href="/dashboard" underline="hover">
                Dashboard
              </Link>
              <Typography color="text.primary">Sales Targets</Typography>
            </Breadcrumbs>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={handleAddNew}>
              Set Target
            </Button>
            <Button variant="contained" color="primary" onClick={() => navigate("/reports/sales-target-report")}>
              View Report
            </Button>
            <Button variant="contained" color="primary" onClick={() => navigate(-1)} startIcon={<ArrowBackIosIcon />}>
              Back
            </Button>
          </Stack>
        </Box>

        {/* Filter Bar */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                select
                fullWidth
                label="Select Year"
                size="small"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <TextField
                select
                fullWidth
                label="Select Month"
                size="small"
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              >
                {monthOptions.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchTargetsData}
                disabled={loading}
                sx={{ height: 40, px: 3, fontWeight: 600 }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Data Table */}
        {loading ? (
          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 3 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <ShinySkeleton key={i} height={48} borderRadius={6} style={{ marginBottom: 12 }} />
            ))}
          </Paper>
        ) : (
          <Grid className="form_design">
            <MaterialReactTable
              columns={columns}
              data={targets}
              state={{ isLoading: false }}
              initialState={{ pagination: { pageSize: 100, pageIndex: 0 } }}
              enableColumnActions={false}
              enableRowSelection={false}
              enableFullScreenToggle={false}
              enableStickyHeader
              className="mat-react-tbl"
              layoutMode="grid"
              renderTopToolbarCustomActions={({ table }) => (
                <Button
                  onClick={() => exportToExcel(table)}
                  startIcon={<FileDownloadIcon />}
                  sx={{ background: "#1a73e829", textTransform: "none" }}
                >
                  Export To Excel
                </Button>
              )}
            />
          </Grid>
        )}
      </Box>

      {/* Target Edit/Create Modal */}
      <SalesTargetModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchTargetsData}
        initialData={selectedTarget ? { ...selectedTarget, year, month } : null}
        salesPersons={salesPersonsList}
      />
    </Layout>
  );
};

export default SalesTargetList;
